import { createBrowserRouter, Navigate, redirect } from 'react-router';
import { ROLES, ROUTES } from '@/lib/constants';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { AdminRoute } from '@/components/layout/AdminRoute';
import { ProviderRoute } from '@/components/layout/ProviderRoute';
import { AppLayout } from '@/components/layout/AppLayout';

export const router = createBrowserRouter([
  // Public routes (no auth, no layout)
  {
    path: ROUTES.LOGIN,
    lazy: () => import('@/features/auth/LoginPage').then((m) => ({ Component: m.LoginPage })),
  },
  {
    path: ROUTES.SIGNUP,
    lazy: () => import('@/features/auth/SignupPage').then((m) => ({ Component: m.SignupPage })),
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    lazy: () =>
      import('@/features/auth/ForgotPasswordPage').then((m) => ({
        Component: m.ForgotPasswordPage,
      })),
  },
  {
    path: ROUTES.RESET_PASSWORD,
    lazy: () =>
      import('@/features/auth/ResetPasswordPage').then((m) => ({ Component: m.ResetPasswordPage })),
  },
  {
    path: ROUTES.EMAIL_CONFIRMATION,
    lazy: () =>
      import('@/features/auth/EmailConfirmationPage').then((m) => ({
        Component: m.EmailConfirmationPage,
      })),
  },

  // Public routes with AppLayout (header/footer but no auth required)
  {
    element: <AppLayout />,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/public/LandingPage').then((m) => ({ Component: m.LandingPage })),
      },
      {
        path: ROUTES.ABOUT,
        lazy: () => import('@/features/public/AboutPage').then((m) => ({ Component: m.AboutPage })),
      },
      {
        path: ROUTES.PRODUCTS,
        lazy: () =>
          import('@/features/public/ProductsPage').then((m) => ({ Component: m.ProductsPage })),
      },
      {
        path: ROUTES.TECHNOLOGY,
        lazy: () =>
          import('@/features/public/TechnologyPage').then((m) => ({ Component: m.TechnologyPage })),
      },
      {
        path: ROUTES.FAQ,
        lazy: () => import('@/features/public/FaqPage').then((m) => ({ Component: m.FaqPage })),
      },
      {
        path: ROUTES.CONTACT,
        lazy: () =>
          import('@/features/public/ContactPage').then((m) => ({ Component: m.ContactPage })),
      },
      {
        path: ROUTES.TERMS,
        lazy: () => import('@/features/public/TermsPage').then((m) => ({ Component: m.TermsPage })),
      },
      {
        path: ROUTES.PRIVACY,
        lazy: () =>
          import('@/features/public/PrivacyPage').then((m) => ({ Component: m.PrivacyPage })),
      },
      {
        path: ROUTES.INTEGRATIONS_EVEXIA,
        lazy: () =>
          import('@/features/public/EvexiaIntegrationPage').then((m) => ({
            Component: m.EvexiaIntegrationPage,
          })),
      },
      {
        path: ROUTES.BLOG,
        lazy: () =>
          import('@/features/content/BlogListPage').then((m) => ({ Component: m.BlogListPage })),
      },
      {
        path: ROUTES.BLOG_POST,
        lazy: () =>
          import('@/features/content/BlogPostPage').then((m) => ({ Component: m.BlogPostPage })),
      },
      // Reference pages (category + article views) — more specific routes first
      {
        path: ROUTES.RESOURCE_ARTICLE,
        lazy: () =>
          import('@/features/content/ReferencePage').then((m) => ({ Component: m.ReferencePage })),
      },
      {
        path: ROUTES.RESOURCE_CATEGORY,
        lazy: () =>
          import('@/features/content/ReferencePage').then((m) => ({ Component: m.ReferencePage })),
      },
      {
        path: ROUTES.HELP_CATEGORY_ARTICLE,
        lazy: () =>
          import('@/features/content/HelpReferencePage').then((m) => ({
            Component: m.HelpReferencePage,
          })),
      },
      {
        path: ROUTES.HELP_CATEGORY_VIEW,
        lazy: () =>
          import('@/features/content/HelpReferencePage').then((m) => ({
            Component: m.HelpReferencePage,
          })),
      },
      // Existing routes kept for backward compatibility
      {
        path: ROUTES.HELP_CENTER,
        lazy: () =>
          import('@/features/content/HelpCenterPage').then((m) => ({
            Component: m.HelpCenterPage,
          })),
      },
      {
        path: ROUTES.HELP_ARTICLE,
        lazy: () =>
          import('@/features/content/HelpArticlePage').then((m) => ({
            Component: m.HelpArticlePage,
          })),
      },
      {
        path: ROUTES.RESOURCES,
        lazy: () =>
          import('@/features/content/ResourcesPage').then((m) => ({ Component: m.ResourcesPage })),
      },
      {
        path: ROUTES.RESOURCE,
        lazy: () =>
          import('@/features/content/ResourcePage').then((m) => ({ Component: m.ResourcePage })),
      },
      {
        path: ROUTES.EVENTS,
        lazy: () =>
          import('@/features/content/EventsPage').then((m) => ({ Component: m.EventsPage })),
      },
      {
        path: ROUTES.EVENT,
        lazy: () =>
          import('@/features/content/EventPage').then((m) => ({ Component: m.EventPage })),
      },
      {
        path: ROUTES.QUIZ,
        lazy: () => import('@/features/quiz/QuizPage').then((m) => ({ Component: m.QuizPage })),
      },
    ],
  },

  // Protected routes (require authentication)
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: ROUTES.DASHBOARD,
            lazy: () =>
              import('@/features/dashboard/DashboardPage').then((m) => ({
                Component: m.DashboardPage,
              })),
          },
          {
            path: ROUTES.PATIENTS,
            lazy: () =>
              import('@/features/patients/PatientListPage').then((m) => ({
                Component: m.PatientListPage,
              })),
          },
          {
            path: ROUTES.PATIENT_NEW,
            lazy: () =>
              import('@/features/patient-form/AddPatientPage').then((m) => ({
                Component: m.AddPatientPage,
              })),
          },
          {
            path: ROUTES.PATIENT_NEW_UPLOAD,
            lazy: () =>
              import('@/features/patient-form/UploadLabPage').then((m) => ({
                Component: m.UploadLabPage,
              })),
          },
          {
            path: ROUTES.PATIENT_NEW_ACTION,
            lazy: () =>
              import('@/features/patient-form/PatientActionChoicePage').then((m) => ({
                Component: m.PatientActionChoicePage,
              })),
          },
          {
            path: ROUTES.PATIENT_NEW_AXISMATRIX,
            lazy: () =>
              import('@/features/axis-matrix/AxisMatrixSendPage').then((m) => ({
                Component: m.AxisMatrixSendPage,
              })),
          },
          {
            path: '/patients/new/pecm',
            loader: ({ request }) => {
              const url = new URL(request.url);
              return redirect(`${ROUTES.PATIENT_NEW_AXISMATRIX}${url.search}`);
            },
          },
          {
            path: ROUTES.PATIENT_NEW_CONFIRM,
            lazy: () =>
              import('@/features/patient-form/ConfirmMarkersPage').then((m) => ({
                Component: m.ConfirmMarkersPage,
              })),
          },
          {
            path: ROUTES.PATIENT_NEW_SYMPTOMS,
            lazy: () =>
              import('@/features/patient-form/SymptomTrackerPage').then((m) => ({
                Component: m.SymptomTrackerPage,
              })),
          },
          {
            path: ROUTES.PATIENT_PROFILE,
            lazy: () =>
              import('@/features/patients/PatientProfilePage').then((m) => ({
                Component: m.PatientProfilePage,
              })),
          },
          {
            path: ROUTES.PATIENT_AXISMATRIX_TAKE,
            lazy: () =>
              import('@/features/axis-matrix/ProviderProxyQuizPage').then((m) => ({
                Component: m.ProviderProxyQuizPage,
              })),
          },
          {
            path: ROUTES.PATIENT_REVIEW,
            lazy: () =>
              import('@/features/patients/PatientReviewPage').then((m) => ({
                Component: m.PatientReviewPage,
              })),
          },
          {
            path: ROUTES.PATIENT_RESULTS,
            lazy: () =>
              import('@/features/patients/PatientResultsPage').then((m) => ({
                Component: m.PatientResultsPage,
              })),
          },
          {
            path: ROUTES.REPORTS_HORMONE,
            lazy: () =>
              import('@/features/reports/HormoneReportPage').then((m) => ({
                Component: m.HormoneReportPage,
              })),
          },
          {
            path: ROUTES.REPORTS_FORMULATIONS,
            lazy: () =>
              import('@/features/reports/FormulationsReportPage').then((m) => ({
                Component: m.FormulationsReportPage,
              })),
          },
          {
            path: ROUTES.REPORTS_LIFESTYLE,
            lazy: () =>
              import('@/features/reports/LifestyleReportPage').then((m) => ({
                Component: m.LifestyleReportPage,
              })),
          },
          {
            path: ROUTES.SHOPIFY_CART,
            lazy: () =>
              import('@/features/shopify/ProviderCartPage').then((m) => ({
                Component: m.ProviderCartPage,
              })),
          },
          {
            path: ROUTES.PROFILE,
            lazy: () =>
              import('@/features/profile/PractitionerProfilePage').then((m) => ({
                Component: m.PractitionerProfilePage,
              })),
          },
          {
            path: ROUTES.ACCOUNT,
            lazy: () =>
              import('@/features/profile/AccountOnboardingPage').then((m) => ({
                Component: m.AccountOnboardingPage,
              })),
          },
          {
            path: ROUTES.FULLSCRIPT_CALLBACK,
            lazy: () =>
              import('@/features/fullscript/FullscriptCallbackPage').then((m) => ({
                Component: m.FullscriptCallbackPage,
              })),
          },
          {
            path: ROUTES.AXISMATRIX_APPROVALS,
            lazy: () =>
              import('@/features/axis-matrix/AxisMatrixApprovalsPage').then((m) => ({
                Component: m.AxisMatrixApprovalsPage,
              })),
          },
          { path: '/pecm-approvals', loader: () => redirect(ROUTES.AXISMATRIX_APPROVALS) },
          {
            path: `${ROUTES.AXISMATRIX_RESULTS}/:hash`,
            lazy: () =>
              import('@/features/axis-matrix/QuizFormulationsPage').then((m) => ({
                Component: m.QuizFormulationsPage,
              })),
          },
          {
            path: '/pecm-results/:hash',
            loader: ({ params, request }) => {
              const search = new URL(request.url).search;
              return redirect(`${ROUTES.AXISMATRIX_RESULTS}/${params.hash ?? ''}${search}`);
            },
          },

          // Practitioner-only routes
          {
            element: <ProviderRoute />,
            children: [
              {
                path: ROUTES.SHOP_FOR_CLIENT,
                lazy: () =>
                  import('@/features/shop-for-client/ShopForClientPage').then((m) => ({
                    Component: m.ShopForClientPage,
                  })),
              },
            ],
          },

          // Admin routes
          {
            element: <AdminRoute />,
            children: [
              {
                path: ROUTES.ADMIN_PROFILE,
                lazy: () =>
                  import('@/features/admin/AdminProfilePage').then((m) => ({
                    Component: m.AdminProfilePage,
                  })),
              },
              {
                path: ROUTES.ADMIN_DASHBOARD,
                lazy: () =>
                  import('@/features/admin/AdminDashboardPage').then((m) => ({
                    Component: m.AdminDashboardPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_PATIENTS,
                lazy: () =>
                  import('@/features/admin/AdminPatientsPage').then((m) => ({
                    Component: m.AdminPatientsPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_PATIENT_MERGE,
                lazy: () =>
                  import('@/features/admin/AdminPatientMergePage').then((m) => ({
                    Component: m.AdminPatientMergePage,
                  })),
              },
              {
                path: ROUTES.ADMIN_QUEUE,
                lazy: () =>
                  import('@/features/admin/AdminQueuePage').then((m) => ({
                    Component: m.AdminQueuePage,
                  })),
              },
              {
                path: ROUTES.ADMIN_PRACTITIONERS,
                lazy: () =>
                  import('@/features/admin/AdminPractitionersPage').then((m) => ({
                    Component: m.AdminPractitionersPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_PRACTITIONER_DETAIL,
                lazy: () =>
                  import('@/features/admin/AdminPractitionerDetailPage').then((m) => ({
                    Component: m.AdminPractitionerDetailPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_USERS,
                lazy: () =>
                  import('@/features/admin/AdminUsersPage').then((m) => ({
                    Component: m.AdminUsersPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_COMMENTS,
                lazy: () =>
                  import('@/features/admin/AdminCommentsPage').then((m) => ({
                    Component: m.AdminCommentsPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_SYSTEM,
                lazy: () =>
                  import('@/features/admin/AdminSystemDataPage').then((m) => ({
                    Component: m.AdminSystemDataPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_2FA,
                lazy: () =>
                  import('@/features/admin/Admin2FAPage').then((m) => ({
                    Component: m.Admin2FAPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_HORMONES,
                lazy: () =>
                  import('@/features/admin/EditHormonesPage').then((m) => ({
                    Component: m.EditHormonesPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_TRIGGERS,
                lazy: () =>
                  import('@/features/admin/EditTriggersPage').then((m) => ({
                    Component: m.EditTriggersPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_HORMONE_LEVELS,
                lazy: () =>
                  import('@/features/admin/EditHormoneLevelsPage').then((m) => ({
                    Component: m.EditHormoneLevelsPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_CONFIG,
                lazy: () =>
                  import('@/features/admin/AdminConfigPage').then((m) => ({
                    Component: m.AdminConfigPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_SESSIONS,
                lazy: () =>
                  import('@/features/admin/sessions/AdminSessionsPage').then((m) => ({
                    Component: m.AdminSessionsPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_SESSION_DETAIL,
                lazy: () =>
                  import('@/features/admin/sessions/SessionPlayerPage').then((m) => ({
                    Component: m.SessionPlayerPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_SETTINGS,
                lazy: () =>
                  import('@/features/admin/settings/AdminSettingsPage').then((m) => ({
                    Component: m.AdminSettingsPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_FEATURE_FLAGS,
                lazy: () =>
                  import('@/features/admin/AdminFeatureFlagsPage').then((m) => ({
                    Component: m.AdminFeatureFlagsPage,
                  })),
              },
              {
                lazy: () =>
                  import('@/features/admin/formula-builder/FormulaBuilderFlagGuard').then((m) => ({
                    Component: m.FormulaBuilderFlagGuard,
                  })),
                children: [
                  {
                    path: ROUTES.ADMIN_ALGORITHM_MANAGEMENT,
                    lazy: () =>
                      import('@/features/admin/formula-builder/AlgorithmManagementPage').then(
                        (m) => ({ Component: m.AlgorithmManagementPage }),
                      ),
                  },
                ],
              },
              // Redirect old formula-builder URL to algorithm-management
              {
                path: ROUTES.ADMIN_FORMULA_BUILDER,
                element: <Navigate to={ROUTES.ADMIN_ALGORITHM_MANAGEMENT} replace />,
              },
              {
                lazy: () =>
                  import('@/features/admin/axis-matrix/AxisMatrixFlagGuard').then((m) => ({
                    Component: m.AxisMatrixFlagGuard,
                  })),
                children: [
                  {
                    path: ROUTES.ADMIN_AXISMATRIX_MANAGEMENT,
                    lazy: () =>
                      import('@/features/admin/axis-matrix/AxisMatrixManagementPage').then((m) => ({
                        Component: m.AxisMatrixManagementPage,
                      })),
                  },
                ],
              },
              {
                path: ROUTES.ADMIN_TELEMETRY,
                lazy: () =>
                  import('@/features/admin/telemetry/TelemetryOverviewPage').then((m) => ({
                    Component: m.TelemetryOverviewPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_TELEMETRY_EMAIL,
                lazy: () =>
                  import('@/features/admin/telemetry/TelemetryEmailPage').then((m) => ({
                    Component: m.TelemetryEmailPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_TELEMETRY_LOGS,
                lazy: () =>
                  import('@/features/admin/telemetry/TelemetryLogsPage').then((m) => ({
                    Component: m.TelemetryLogsPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_TELEMETRY_TRACES,
                lazy: () =>
                  import('@/features/admin/telemetry/TelemetryTracesPage').then((m) => ({
                    Component: m.TelemetryTracesPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_TELEMETRY_TRACE_DETAIL,
                lazy: () =>
                  import('@/features/admin/telemetry/TelemetryTraceDetailPage').then((m) => ({
                    Component: m.TelemetryTraceDetailPage,
                  })),
              },
            ],
          },

          // Admin + CMS admin routes
          {
            element: <AdminRoute allowedRoles={[ROLES.ADMIN, ROLES.CMS_ADMIN]} />,
            children: [
              {
                path: ROUTES.ADMIN_CONTENT,
                lazy: () =>
                  import('@/features/admin/content/AdminContentPage').then((m) => ({
                    Component: m.AdminContentPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_CONTENT_NEW,
                lazy: () =>
                  import('@/features/admin/content/AdminPostEditor').then((m) => ({
                    Component: m.AdminPostEditor,
                  })),
              },
              {
                path: ROUTES.ADMIN_CONTENT_EDIT,
                lazy: () =>
                  import('@/features/admin/content/AdminPostEditor').then((m) => ({
                    Component: m.AdminPostEditor,
                  })),
              },
              {
                path: ROUTES.ADMIN_CONTENT_CATEGORIES,
                lazy: () =>
                  import('@/features/admin/content/AdminCategoryManager').then((m) => ({
                    Component: m.AdminCategoryManager,
                  })),
              },
              {
                path: ROUTES.ADMIN_CONTACTS,
                lazy: () =>
                  import('@/features/admin/content/AdminContactSubmissions').then((m) => ({
                    Component: m.AdminContactSubmissions,
                  })),
              },
              {
                path: ROUTES.ADMIN_PRODUCT_CATALOG,
                lazy: () =>
                  import('@/features/admin/AdminProductCatalogPage').then((m) => ({
                    Component: m.AdminProductCatalogPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_PRODUCT_CATALOG_PRODUCTS,
                lazy: () =>
                  import('@/features/admin/AdminProductCatalogPage').then((m) => ({
                    Component: m.AdminProductCatalogProductsPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_PRODUCT_CATALOG_VENDORS,
                lazy: () =>
                  import('@/features/admin/AdminProductCatalogPage').then((m) => ({
                    Component: m.AdminProductCatalogVendorsPage,
                  })),
              },
              {
                path: ROUTES.ADMIN_PRODUCT_CATALOG_CATEGORIES,
                lazy: () =>
                  import('@/features/admin/AdminProductCatalogPage').then((m) => ({
                    Component: m.AdminProductCatalogCategoriesPage,
                  })),
              },
              {
                lazy: () =>
                  import('@/features/admin/SesEmailTemplatesFlagGuard').then((m) => ({
                    Component: m.SesEmailTemplatesFlagGuard,
                  })),
                children: [
                  {
                    path: ROUTES.ADMIN_SES_EMAIL_TEMPLATES,
                    lazy: () =>
                      import('@/features/admin/AdminSesEmailTemplatesPage').then((m) => ({
                        Component: m.AdminSesEmailTemplatesPage,
                      })),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // 404
  {
    path: '*',
    lazy: () => import('@/features/auth/NotFoundPage').then((m) => ({ Component: m.NotFoundPage })),
  },
]);
